import React, { useState, useEffect, useMemo } from 'react';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import TransactionItem from '../components/TransactionItem';
import { 
  Filter, Search, BarChart3, TrendingUp, TrendingDown, 
  DollarSign, Calendar, Download, ChevronDown, ChevronUp,
  PieChart, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSummary, setShowSummary] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('all'); // 'all' or 0-11

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const fullMonthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                          'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [transactions, filter, searchQuery]);

  const fetchTransactions = async () => {
    try {
      const response = await api.get('/transactions');
      setTransactions(response.data);
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = transactions;

    if (filter === 'expense') {
      filtered = filtered.filter((t) => t.type === 'expense');
    } else if (filter === 'income') {
      filtered = filtered.filter((t) => t.type === 'income');
    } else if (filter === 'business') {
      filtered = filtered.filter((t) => t.type === 'expense' && t.business_expense === true);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.merchant?.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query) ||
          t.category?.toLowerCase().includes(query)
      );
    }

    setFilteredTransactions(filtered);
  };

  // Calculate monthly summary
  const monthlySummary = useMemo(() => {
    const months = {};
    
    // Initialize all months
    monthNames.forEach((name, index) => {
      months[index] = { name, income: 0, expenses: 0, net: 0, transactions: 0 };
    });

    transactions.forEach(t => {
      const dateStr = t.date || '';
      let month, year;
      
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        month = parseInt(parts[0]) - 1;
        year = parseInt(parts[2]);
      } else if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        year = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
      } else {
        return;
      }

      if (year !== selectedYear || month < 0 || month > 11) return;

      const amount = parseFloat(t.amount) || 0;
      if (t.type === 'income') {
        months[month].income += amount;
      } else {
        months[month].expenses += amount;
      }
      months[month].transactions++;
    });

    // Calculate net
    Object.values(months).forEach(m => {
      m.net = m.income - m.expenses;
    });

    return months;
  }, [transactions, selectedYear]);

  // Calculate totals (with month filter support)
  const totals = useMemo(() => {
    let income = 0, expenses = 0;
    if (selectedMonth === 'all') {
      Object.values(monthlySummary).forEach(m => {
        income += m.income;
        expenses += m.expenses;
      });
    } else {
      const monthData = monthlySummary[selectedMonth];
      if (monthData) {
        income = monthData.income;
        expenses = monthData.expenses;
      }
    }
    return { income, expenses, net: income - expenses };
  }, [monthlySummary, selectedMonth]);

  // Calculate category breakdown (with month filter support)
  const categoryBreakdown = useMemo(() => {
    const categories = {};
    transactions.forEach(t => {
      const dateStr = t.date || '';
      let year, month;
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        month = parseInt(parts[0]) - 1;
        year = parseInt(parts[2]);
      } else if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        year = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
      }
      
      if (year !== selectedYear) return;
      if (selectedMonth !== 'all' && month !== parseInt(selectedMonth)) return;
      if (t.type !== 'expense') return;

      const cat = t.category || 'Other';
      categories[cat] = (categories[cat] || 0) + (parseFloat(t.amount) || 0);
    });
    return Object.entries(categories).sort((a, b) => b[1] - a[1]);
  }, [transactions, selectedYear, selectedMonth]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Get unique years from transactions (only years with actual data)
  const availableYears = useMemo(() => {
    const years = new Set();
    transactions.forEach(t => {
      const dateStr = t.date || '';
      let year;
      if (dateStr.includes('/')) {
        year = parseInt(dateStr.split('/')[2]);
      } else if (dateStr.includes('-')) {
        year = parseInt(dateStr.split('-')[0]);
      }
      if (year && year > 2020 && year < 2100) years.add(year);
    });
    // If no years with data, show current year only
    if (years.size === 0) {
      years.add(new Date().getFullYear());
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [transactions]);

  // Get months with data for selected year
  const availableMonths = useMemo(() => {
    const monthsWithData = new Set();
    transactions.forEach(t => {
      const dateStr = t.date || '';
      let year, month;
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        month = parseInt(parts[0]) - 1;
        year = parseInt(parts[2]);
      } else if (dateStr.includes('-')) {
        const parts = dateStr.split('-');
        year = parseInt(parts[0]);
        month = parseInt(parts[1]) - 1;
      }
      if (year === selectedYear && month >= 0 && month <= 11) {
        monthsWithData.add(month);
      }
    });
    return Array.from(monthsWithData).sort((a, b) => a - b);
  }, [transactions, selectedYear]);

  // Colors for pie chart
  const pieColors = [
    '#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', 
    '#eab308', '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6'
  ];

  const handleExportReport = () => {
    const periodLabel = selectedMonth === 'all' 
      ? `Year ${selectedYear}` 
      : `${fullMonthNames[selectedMonth]} ${selectedYear}`;
    
    let report = `FINSCAN USA - FINANCIAL REPORT\n`;
    report += `Period: ${periodLabel}\n`;
    report += `${'='.repeat(60)}\n\n`;
    
    report += `SUMMARY\n`;
    report += `${'-'.repeat(40)}\n`;
    report += `Total Income:     ${formatCurrency(totals.income)}\n`;
    report += `Total Expenses:   ${formatCurrency(totals.expenses)}\n`;
    report += `Net Profit/Loss:  ${formatCurrency(totals.net)}\n`;
    report += `Profit Margin:    ${totals.income > 0 ? ((totals.net / totals.income) * 100).toFixed(1) : 0}%\n\n`;
    
    if (selectedMonth === 'all') {
      report += `MONTHLY BREAKDOWN\n`;
      report += `${'-'.repeat(60)}\n`;
      report += `Month          Income        Expenses      Net\n`;
      report += `${'-'.repeat(60)}\n`;
      
      Object.values(monthlySummary).forEach((m, i) => {
        const monthName = fullMonthNames[i].padEnd(14);
        const income = formatCurrency(m.income).padStart(12);
        const expenses = formatCurrency(m.expenses).padStart(12);
        const net = formatCurrency(m.net).padStart(12);
        report += `${monthName}${income}  ${expenses}  ${net}\n`;
      });
      
      report += `${'-'.repeat(60)}\n`;
      report += `TOTAL         ${formatCurrency(totals.income).padStart(12)}  ${formatCurrency(totals.expenses).padStart(12)}  ${formatCurrency(totals.net).padStart(12)}\n\n`;
    }
    
    report += `EXPENSES BY CATEGORY\n`;
    report += `${'-'.repeat(40)}\n`;
    categoryBreakdown.forEach(([cat, amount]) => {
      const pct = totals.expenses > 0 ? ((amount / totals.expenses) * 100).toFixed(1) : 0;
      report += `${cat.padEnd(20)} ${formatCurrency(amount).padStart(12)}  (${pct}%)\n`;
    });
    
    report += `\n\nGenerated by FinScan USA on ${new Date().toLocaleDateString()}\n`;
    
    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financial_report_${selectedYear}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report downloaded!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#030712] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030712]">
      <Navbar />

      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] tracking-tight mb-2">
              All Transactions
            </h1>
            <p className="text-lg text-muted-foreground">
              View and search through all your expenses and income
            </p>
          </div>
          
          {/* Summary Toggle Button */}
          <button
            onClick={() => setShowSummary(!showSummary)}
            className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all ${
              showSummary 
                ? 'bg-indigo-500 text-white' 
                : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
            }`}
          >
            <BarChart3 className="w-5 h-5" />
            <span>Summary & Reports</span>
            {showSummary ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {/* Summary Panel */}
        {showSummary && (
          <div className="mb-8 p-6 bg-gradient-to-br from-indigo-500/10 via-purple-500/5 to-pink-500/10 border border-indigo-500/20 rounded-2xl">
            {/* Year & Month Selector + Export */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-3">
                <Calendar className="w-5 h-5 text-indigo-400" />
                {/* Year Selector */}
                <select
                  value={selectedYear}
                  onChange={(e) => {
                    setSelectedYear(parseInt(e.target.value));
                    setSelectedMonth('all');
                  }}
                  className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                  data-testid="year-filter"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year} className="bg-[#0f172a]">{year}</option>
                  ))}
                </select>
                
                {/* Month Selector */}
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                  className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white"
                  data-testid="month-filter"
                >
                  <option value="all" className="bg-[#0f172a]">All Months</option>
                  {monthNames.map((name, index) => (
                    <option 
                      key={index} 
                      value={index} 
                      className="bg-[#0f172a]"
                      disabled={!availableMonths.includes(index)}
                    >
                      {name} {!availableMonths.includes(index) ? '(no data)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleExportReport}
                className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                data-testid="export-report-btn"
              >
                <Download className="w-4 h-4" />
                Export Report
              </button>
            </div>

            {/* Period Label */}
            <div className="text-center mb-4">
              <span className="text-sm text-muted-foreground">
                Showing: {selectedMonth === 'all' ? `All of ${selectedYear}` : `${fullMonthNames[selectedMonth]} ${selectedYear}`}
              </span>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                  <span className="text-sm text-gray-400">TOTAL INCOME</span>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(totals.income)}</p>
              </div>
              
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                  <span className="text-sm text-gray-400">TOTAL EXPENSES</span>
                </div>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(totals.expenses)}</p>
              </div>
              
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-blue-400" />
                  <span className="text-sm text-gray-400">NET PROFIT</span>
                </div>
                <p className={`text-2xl font-bold ${totals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(totals.net)}
                </p>
              </div>
              
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <div className="flex items-center gap-2 mb-2">
                  <PieChart className="w-5 h-5 text-purple-400" />
                  <span className="text-sm text-gray-400">PROFIT MARGIN</span>
                </div>
                <p className={`text-2xl font-bold ${totals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {totals.income > 0 ? ((totals.net / totals.income) * 100).toFixed(1) : 0}%
                </p>
              </div>
            </div>

            {/* Charts Row */}
            <div className="grid lg:grid-cols-2 gap-6 mb-6">
              {/* Monthly Bar Chart */}
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <h3 className="text-sm font-semibold text-gray-400 mb-4">INCOME VS EXPENSES</h3>
                <div className="h-48 flex items-end justify-between gap-1">
                  {Object.values(monthlySummary).map((m, i) => {
                    const maxValue = Math.max(
                      ...Object.values(monthlySummary).map(x => Math.max(x.income, x.expenses))
                    ) || 1;
                    const incomeHeight = (m.income / maxValue) * 100;
                    const expenseHeight = (m.expenses / maxValue) * 100;
                    
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full flex gap-0.5 h-36 items-end">
                          <div 
                            className="flex-1 bg-green-500 rounded-t-sm transition-all hover:bg-green-400"
                            style={{ height: `${incomeHeight}%` }}
                            title={`Income: ${formatCurrency(m.income)}`}
                          />
                          <div 
                            className="flex-1 bg-red-500 rounded-t-sm transition-all hover:bg-red-400"
                            style={{ height: `${expenseHeight}%` }}
                            title={`Expenses: ${formatCurrency(m.expenses)}`}
                          />
                        </div>
                        <span className="text-[10px] text-gray-500">{m.name}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-4 mt-3">
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-green-500 rounded-sm" />
                    <span className="text-xs text-gray-400">Income</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-red-500 rounded-sm" />
                    <span className="text-xs text-gray-400">Expenses</span>
                  </div>
                </div>
              </div>

              {/* Expenses by Category */}
              <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10">
                <h3 className="text-sm font-semibold text-gray-400 mb-4">EXPENSES BY CATEGORY</h3>
                {categoryBreakdown.length > 0 ? (
                  <div className="space-y-2">
                    {categoryBreakdown.slice(0, 6).map(([cat, amount], i) => {
                      const pct = totals.expenses > 0 ? (amount / totals.expenses) * 100 : 0;
                      return (
                        <div key={cat}>
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-300">{cat}</span>
                            <span className="text-gray-400">{formatCurrency(amount)} ({pct.toFixed(0)}%)</span>
                          </div>
                          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${pct}%`,
                                backgroundColor: pieColors[i % pieColors.length]
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No expense data for {selectedYear}</p>
                )}
              </div>
            </div>

            {/* Monthly Table */}
            <div className="p-4 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 overflow-x-auto">
              <h3 className="text-sm font-semibold text-gray-400 mb-4">MONTHLY BREAKDOWN</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-white/10">
                    <th className="pb-2 font-medium">Month</th>
                    <th className="pb-2 font-medium text-right">Income</th>
                    <th className="pb-2 font-medium text-right">Expenses</th>
                    <th className="pb-2 font-medium text-right">Net</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(monthlySummary).map((m, i) => (
                    <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-2 text-gray-300">{m.name}</td>
                      <td className="py-2 text-right text-green-400 font-mono">{formatCurrency(m.income)}</td>
                      <td className="py-2 text-right text-red-400 font-mono">{formatCurrency(m.expenses)}</td>
                      <td className={`py-2 text-right font-mono font-semibold ${m.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(m.net)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t-2 border-white/20">
                    <td className="pt-3 text-white">TOTAL</td>
                    <td className="pt-3 text-right text-green-400 font-mono">{formatCurrency(totals.income)}</td>
                    <td className="pt-3 text-right text-red-400 font-mono">{formatCurrency(totals.expenses)}</td>
                    <td className={`pt-3 text-right font-mono ${totals.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {formatCurrency(totals.net)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-input"
              placeholder="Search by merchant, category, or description..."
              className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex flex-wrap gap-2 p-1 bg-white/5 rounded-xl">
            <button
              onClick={() => setFilter('all')}
              data-testid="filter-all"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'all'
                  ? 'bg-white text-black'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('expense')}
              data-testid="filter-expense"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'expense'
                  ? 'bg-red-500 text-white'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              Expenses
            </button>
            <button
              onClick={() => setFilter('income')}
              data-testid="filter-income"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'income'
                  ? 'bg-green-500 text-white'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              Income
            </button>
            <button
              onClick={() => setFilter('business')}
              data-testid="filter-business"
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                filter === 'business'
                  ? 'bg-indigo-500 text-white'
                  : 'text-muted-foreground hover:text-white'
              }`}
            >
              Business
            </button>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-muted-foreground">
          Showing {filteredTransactions.length} of {transactions.length} transactions
        </div>

        {/* Transaction List */}
        <div className="space-y-3">
          {filteredTransactions.length === 0 ? (
            <div className="text-center py-12 bg-white/5 rounded-xl">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">No transactions found</p>
            </div>
          ) : (
            filteredTransactions.map((transaction) => (
              <TransactionItem
                key={transaction.id}
                transaction={transaction}
                onUpdate={fetchTransactions}
                onDelete={fetchTransactions}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Transactions;
